import React from "react";

export type BadgeVariant = "default" | "success" | "warning" | "error" | "info";
export type BadgeSize = "xs" | "sm";

interface BadgeProps {
  label: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  uppercase?: boolean;
  className?: string;
}

const COLOR: Record<BadgeVariant, string> = {
  default: "var(--color-fg-muted)",
  success: "var(--color-success)",
  warning: "var(--color-warning)",
  error: "var(--color-error)",
  info: "var(--color-accent)",
};

const SIZE_FONT: Record<BadgeSize, string> = {
  xs: "var(--font-size-xx-small)",
  sm: "var(--font-size-small)",
};

const SIZE_PADDING: Record<BadgeSize, string> = {
  xs: "var(--spacing-xs)",
  sm: "var(--spacing-sm)",
};

/**
 * Static status label — non-interactive, inline. Use for row-level state,
 * count tags, "Auto" / "Suggested" markers. For interactive filters or
 * removable tags, use Chip.
 */
export const Badge: React.FC<BadgeProps> = ({
  label,
  variant = "default",
  size = "xs",
  uppercase = true,
  className,
}) => {
  const color = COLOR[variant];
  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: SIZE_PADDING[size],
        fontSize: SIZE_FONT[size],
        fontFamily: "inherit",
        fontWeight: "var(--font-weight-semibold)",
        // Uppercase has no descenders, so an inherited line box leaves the caps
        // riding high and the padding looking bottom-heavy.
        lineHeight: 1,
        color,
        background: "transparent",
        border: `1px solid ${color}`,
        borderRadius: "var(--radius-app)",
        textTransform: uppercase ? "uppercase" : "none",
        letterSpacing: uppercase ? "0.04em" : undefined,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};
